// File: src/pages/LoginPage.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";

export default function LoginPage({ onLoginSuccess }) {
  const [role, setRole] = useState("user"); // default role
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const navigate = useNavigate();

  // 🔐 FIXED ADMIN CREDENTIALS
  const ADMIN_EMAIL = "admin@meetragreen.com";
  const ADMIN_PASSWORD = "meetragreen";

  // 🔐 FIXED EMPLOYEE CREDENTIALS
  const EMPLOYEE_EMAIL = "employee@meetragreen.com";
  const EMPLOYEE_PASSWORD = "meetragreen";

  /* ================= AUTO REDIRECT IF ALREADY LOGGED IN ================= */
  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));

      if (storedUser?.role && !sessionStorage.getItem("redirected")) {
        if (storedUser.role === "admin") {
          navigate("/admin-dashboard");
        } else if (storedUser.role === "employee") {
          navigate("/employee-dashboard");
        } else {
          navigate("/dashboard");
        }

        sessionStorage.setItem("redirected", "true");
      }
    } catch (error) {
      console.error("Error checking stored user:", error);
    }
  }, [navigate]);

  /* ================= NAVIGATION HANDLERS ================= */
  const handleSignup = () => navigate("/signup");
  const handleForgotPassword = () => navigate("/forgot-password");

  /* ================= LOGIN HANDLER ================= */
  const handleLogin = async () => {
    if (!email || !password) {
      alert("Please enter your credentials.");
      return;
    }

    /* ================= ADMIN LOGIN ================= */
    if (role === "admin") {
      if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
        const adminData = {
          role: "admin",
          email: ADMIN_EMAIL,
          fullName: "System Admin",
        };

        localStorage.setItem("user", JSON.stringify(adminData));
        sessionStorage.setItem("redirected", "true");
        onLoginSuccess?.(adminData);
        navigate("/admin-dashboard");
      } else {
        alert("Invalid admin credentials");
      }
      return;
    }

    /* ================= FIXED EMPLOYEE LOGIN ================= */
    if (email === EMPLOYEE_EMAIL && password === EMPLOYEE_PASSWORD) {
      const employeeData = {
        role: "employee",
        email: EMPLOYEE_EMAIL,
        fullName: "Company Employee",
      };

      localStorage.setItem("user", JSON.stringify(employeeData));
      sessionStorage.setItem("redirected", "true");
      onLoginSuccess?.(employeeData);
      navigate("/employee-dashboard");
      return;
    }

    /* ================= BACKEND USER LOGIN ================= */
    try {
      const response = await fetch(
        "https://meetra-green-energy-backend.onrender.com/api/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        }
      );

      const text = await response.text();
      let data;

      try {
        data = JSON.parse(text);
      } catch (err) {
        console.error("Invalid JSON response:", text);
        alert("Server returned invalid response. Check backend URL.");
        return;
      }

      if (!response.ok) {
        alert(data.error || "Login failed");
        return;
      }

      const userData = data.user || {
        role: "user",
        email,
      };

      localStorage.setItem("user", JSON.stringify(userData));
      sessionStorage.setItem("redirected", "true");
      onLoginSuccess?.(userData);
      navigate("/dashboard");
    } catch (error) {
      console.error("Login Error:", error);
      alert("Server not running or connection failed");
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">

        {/* Logo */}
        <div className="logo-small">
          <img
            src="https://res.cloudinary.com/dbnfm5a06/image/upload/v1765776384/LOGO_qf0evm.png"
            alt="Meetra Logo"
          />
        </div>

        {/* Role Switch */}
        <div className="role-switch">
          <button
            type="button"
            onClick={() => setRole("admin")}
            className={role === "admin" ? "active" : ""}
          >
            Admin
          </button>

          <button
            type="button"
            onClick={() => setRole("user")}
            className={role === "user" ? "active" : ""}
          >
            User
          </button>
        </div>

        {/* Email / Employee ID */}
        <div className="form-group">
          <input
            type="text"
            placeholder="Email or Employee ID"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {/* Password */}
        <div className="form-group">
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {/* Forgot Password */}
        {role === "user" && (
          <div className="forgot-password-wrapper">
            <button
              type="button"
              className="forgot-password-link"
              onClick={handleForgotPassword}
            >
              Forgot Password?
            </button>
          </div>
        )}

        {/* Login Button */}
        <button type="button" className="login-btn" onClick={handleLogin}>
          Sign In as {role === "admin" ? "Admin" : "User"}
        </button>

        {/* Signup */}
        {role === "user" && (
          <p className="signup-text">
            New User?{" "}
            <button
              type="button"
              className="signup-link"
              onClick={handleSignup}
            >
              Sign Up
            </button>
          </p>
        )}
      </div>
    </div>
  );
}